import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    cardImage = "cardImage",
    cardTitle = "cardTitle",
    cardContent = "cardContent",
    cardLink = "cardLink",
    overrideTitle = "overrideTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cardImage]: string;
        [fields.cardTitle]: string;
        [fields.cardContent]: string;
        [fields.cardLink]: LinkFieldValue;
        [fields.overrideTitle]: string;
    };
    extendedStyles?: CardStyles;
}

export interface CardStyles {
    cardWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    clickableStyles?: InjectableCss;
    titleFont: InjectableCss;
}

export const imageStyles: CardStyles = {
    cardWrapper: {
        css: css`
            box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
                0 1px 5px 0 rgba(0, 0, 0, 0.12);
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            img {
                width: 100%;
            }
        `,
    },
    clickableStyles: {
        css: css`
            flex-direction: column;
            height: 100%;
            color: #212529;
            &:hover {
                color: #212529;
                text-decoration: none;
            }
        `,
    },
    contentWrapper: {
        css: css`
            padding: 10px;
            border-top: none;
        `,
    },
    titleWrapper: {
        css: css`
            font-style: italic;
            font-weight: bold;
        `,
    },
    titleFont: {
        css: css`
            color: #8b8b8b;
            font-size: 24px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const cardLink = getLink(state, ownProps.fields.cardLink);
    return {
        cardLink,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const PromotionsCard: React.FunctionComponent<Props> = ({ cardLink, fields, ...otherProps }: Props) => {
    const [styles] = React.useState(() => mergeToNew(imageStyles, otherProps.extendedStyles));

    return (
        <StyledWrapper {...styles.cardWrapper}>
            <Clickable href={cardLink?.url} {...styles.clickableStyles}>
                <StyledWrapper {...styles.imageWrapper}>
                    <img src={fields.cardImage} alt={translate("home")} />
                </StyledWrapper>
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.titleFont} variant="h3">
                            {" "}
                            {fields.cardTitle}{" "}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <>{parse(fields.cardContent, parserOptions)}</>
                    </StyledWrapper>
                </StyledWrapper>
            </Clickable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PromotionsCard),
    definition: {
        group: "Basic",
        icon: "Logo",
        fieldDefinitions: [
            {
                name: fields.cardImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.cardTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.cardContent,
                displayName: "Card Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                fieldType: "General",
                name: fields.cardLink,
                displayName: "Card Bottom Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.overrideTitle,
                displayName: "Override Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
